// Thumbs rating on an assistant turn.
//
// The ONLY direct quality measurement this product has. Everything else on the
// dashboard is a proxy — citation counts, refusal rates, how often a draft gets
// revised — and proxies can all look healthy while the answers are wrong. That is
// this app's defining failure mode, so the one control that measures it directly
// earns its place in the pane.
//
// Deliberately understated: no prompt, no "how did I do?", no modal. An HR person
// mid-task should be able to ignore it completely, and a rating widget that nags
// gets rated to make it go away, which is worse than no data.

import { useRef, useState } from "react";
import { sendFeedback } from "../api/client";

export type Rating = "up" | "down" | null;

/** Matches the server's cap (routes/feedback.ts). Enforced here too so the box stops
 *  accepting characters rather than silently truncating them on the way in — a box
 *  that takes text and then discards it is worse than one that stops. */
const COMMENT_MAX = 1000;

export function Feedback({
  usageEventId,
  value,
  onChange,
}: {
  usageEventId: number;
  value: Rating;
  onChange: (next: Rating) => void;
}) {
  const [busy, setBusy] = useState(false);
  // THE PROMPT IS A SECOND STEP, never a gate. The rating is already saved by the
  // time this opens, so ignoring the box, clicking away, or closing the pane all
  // leave a recorded thumb. That ordering is the whole design: a prompt that must be
  // answered gets answered carelessly, and this is the one signal in the product that
  // is worth having honest.
  const [asking, setAsking] = useState(false);
  const [text, setText] = useState("");
  const [sent, setSent] = useState(false);
  const boxRef = useRef<HTMLTextAreaElement | null>(null);

  async function rate(next: Rating) {
    if (busy) return;
    // Clicking the active thumb clears it, so a mis-click is undoable without
    // hunting for an undo affordance.
    const resolved: Rating = value === next ? null : next;
    setBusy(true);
    // Optimistic: the thumb flips immediately and stays flipped even if the write
    // fails. A rating is not worth an error state in front of the user — the
    // failure is logged in sendFeedback, which is where someone debugging will look.
    onChange(resolved);
    // THUMBS-DOWN ONLY (Noah, 2026-08-26). It shipped on both for one day.
    //
    // The case for asking on a thumbs-up was that knowing which answers landed tells
    // you where the corpus already works. The case against is stronger in this
    // product: a box under every rating turns the lightest control in the pane into a
    // two-step interaction, and the people rating most often are HR staff mid-task.
    // Ask on the positive path and the cost is paid on EVERY rating, while the thing
    // you actually need to act on is the negative one.
    //
    // A thumbs-up already carries its own signal — the answer, its citations and the
    // rate among rated turns are all on the dashboard. A thumbs-down without words is
    // the only rating that leaves you unable to do anything about it.
    //
    // Clearing a rating closes the box and clears the comment with it, server-side
    // too: an explanation of a rating that no longer exists is orphaned prose about an
    // employee's HR question.
    const wantsComment = resolved === "down";
    setAsking(wantsComment);
    setSent(false);
    setText("");
    await sendFeedback(usageEventId, resolved);
    setBusy(false);
    // Focus after the write so the caret does not jump mid-render.
    if (wantsComment) requestAnimationFrame(() => boxRef.current?.focus());
  }

  async function submitComment() {
    const trimmed = text.trim();
    if (!trimmed) return dismiss();
    setBusy(true);
    setSent(true);
    setAsking(false);
    await sendFeedback(usageEventId, value, trimmed);
    setBusy(false);
  }

  function dismiss() {
    setAsking(false);
    setText("");
  }

  return (
    <div className="tp-feedback-wrap">
    <div className="tp-feedback">
      <span className="tp-feedback-label">Was this helpful?</span>
      <button
        type="button"
        className={`tp-feedback-btn${value === "up" ? " is-on" : ""}`}
        aria-pressed={value === "up"}
        aria-label="Helpful"
        title="Helpful"
        onClick={() => void rate("up")}
      >
        <ThumbIcon />
      </button>
      <button
        type="button"
        className={`tp-feedback-btn is-down${value === "down" ? " is-on" : ""}`}
        aria-pressed={value === "down"}
        aria-label="Not helpful"
        title="Not helpful"
        onClick={() => void rate("down")}
      >
        <ThumbIcon />
      </button>
    </div>

      {asking && (
        <div className="tp-feedback-ask">
          <label className="tp-feedback-ask-label" htmlFor={`fb-${usageEventId}`}>
            What was wrong with it? (optional)
          </label>
          <textarea
            id={`fb-${usageEventId}`}
            ref={boxRef}
            className="tp-feedback-box"
            rows={2}
            maxLength={COMMENT_MAX}
            value={text}
            placeholder="It missed the part about…"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends, Shift+Enter makes a newline — the convention everywhere
              // else in this pane's composer, so the muscle memory carries over.
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void submitComment();
              }
              if (e.key === "Escape") dismiss();
            }}
          />
          <div className="tp-feedback-ask-actions">
            <button type="button" className="tp-feedback-send" disabled={busy || !text.trim()} onClick={() => void submitComment()}>
              Send
            </button>
            <button type="button" className="tp-feedback-skip" onClick={dismiss}>
              Skip
            </button>
          </div>
        </div>
      )}
      {sent && <div className="tp-feedback-thanks">Thanks — that helps.</div>}
    </div>
  );
}

/** One glyph, flipped with CSS for the down state — half the markup, and the two
 *  can never drift apart visually. */
function ThumbIcon() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M6.6 14h4.9c.7 0 1.3-.4 1.5-1.1l1.4-4.1c.05-.15.08-.3.08-.46V7.2c0-.66-.54-1.2-1.2-1.2H9.9l.5-2.5c.05-.27 0-.55-.17-.77l-.6-.73L5.9 6.1c-.2.24-.3.54-.3.85V13c0 .55.45 1 1 1zM2.8 6h1.4c.33 0 .6.27.6.6v6.8c0 .33-.27.6-.6.6H2.8a.6.6 0 0 1-.6-.6V6.6c0-.33.27-.6.6-.6z"
      />
    </svg>
  );
}
